import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Play, Square, Trash2, X, Radar, Target, Briefcase, Loader2, AlertCircle, CheckCircle2, Pause, Activity, Users, Sparkles } from 'lucide-react';
import api from '@/lib/api';
import { useToast } from '../../contexts/ToastContext';

// ── Status meta helpers ────────────────────────────────────────────────────
const STATUS_META = {
  idle:          { label: 'Idle',          color: 'bg-slate-100 text-slate-600 border-slate-200',   Icon: Pause },
  running:       { label: 'Running',       color: 'bg-blue-50 text-[#2563eb] border-blue-200',      Icon: Activity, pulse: true },
  hunting:       { label: 'Hunting',       color: 'bg-blue-50 text-[#2563eb] border-blue-200',      Icon: Radar,    pulse: true },
  paused:        { label: 'Paused',        color: 'bg-amber-50 text-amber-700 border-amber-200',    Icon: Pause },
  stopped:       { label: 'Stopped',       color: 'bg-slate-100 text-slate-500 border-slate-200',   Icon: Square },
  goal_reached:  { label: 'Goal Reached',  color: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
  completed:     { label: 'Completed',     color: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
  safety_stopped:{ label: 'Safety Stop',   color: 'bg-rose-50 text-rose-700 border-rose-200',       Icon: AlertCircle },
  error:         { label: 'Error',         color: 'bg-rose-50 text-rose-700 border-rose-200',       Icon: AlertCircle },
};

const statusMeta = (s) => STATUS_META[s] || STATUS_META.idle;

const isActiveStatus = (s) => s === 'running' || s === 'hunting';

export default function SourcingAgentsPage() {
  const { addToast } = useToast();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [startingId, setStartingId] = useState(null);
  const [stoppingId, setStoppingId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    job_id: '',
    goal: '',
    min_score: 60,
    max_candidates: 25,
  });

  const pollRef = useRef(null);

  const fetchAgents = useCallback(async () => {
    try {
      const { data } = await api.get('/sourcing-agents');
      setAgents(Array.isArray(data) ? data : []);
    } catch (err) {
      logger.error('[SourcingAgents] fetchAgents failed:', err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchJobs = useCallback(async () => {
    try {
      const { data } = await api.get('/jobs');
      setJobs(Array.isArray(data) ? data : (data?.jobs || []));
    } catch (err) {
      logger.error('Failed to load jobs', err);
    }
  }, []);

  useEffect(() => {
    fetchAgents();
    fetchJobs();
  }, [fetchAgents, fetchJobs]);

  useEffect(() => {
    const anyActive = agents.some((a) => isActiveStatus(a.status));
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (anyActive) {
      pollRef.current = setInterval(fetchAgents, 5000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [agents, fetchAgents]);

  const openCreate = () => {
    setForm({ name: '', job_id: '', goal: '', min_score: 60, max_candidates: 25 });
    setShowCreate(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.job_id) {
      addToast('Name and Job are required', 'error');
      return;
    }
    setCreating(true);
    try {
      await api.post('/sourcing-agents', {
        name: form.name.trim(),
        job_id: form.job_id,
        goal: form.goal.trim(),
        min_score: Number(form.min_score) || 60,
        max_candidates: Number(form.max_candidates) || 25,
      });
      setShowCreate(false);
      addToast('Sourcing agent created', 'success');
      await fetchAgents();
    } catch (err) {
      addToast(err.response?.data?.detail || 'Failed to create agent', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleStart = async (agent) => {
    setStartingId(agent.agent_id);
    try {
      await api.post(`/sourcing-agents/${agent.agent_id}/start`);
      addToast(`${agent.name} started`, 'success');
      await fetchAgents();
    } catch (err) {
      addToast(err.response?.data?.detail || 'Failed to start agent', 'error');
    } finally {
      setStartingId(null);
    }
  };

  const handleStop = async (agent) => {
    setStoppingId(agent.agent_id);
    try {
      await api.post(`/sourcing-agents/${agent.agent_id}/stop`);
      addToast(`${agent.name} stopped`, 'success');
      await fetchAgents();
    } catch (err) {
      addToast(err.response?.data?.detail || 'Failed to stop agent', 'error');
    } finally {
      setStoppingId(null);
    }
  };

  const handleDelete = async (agent) => {
    if (!window.confirm(`Delete sourcing agent "${agent.name}"?`)) return;
    try {
      await api.delete(`/sourcing-agents/${agent.agent_id}`);
      addToast('Agent deleted', 'success');
      await fetchAgents();
    } catch (err) {
      addToast(err.response?.data?.detail || 'Failed to delete agent', 'error');
    }
  };

  const jobTitleFor = (jobId) => {
    const j = jobs.find((x) => x.job_id === jobId);
    return j?.title || jobId?.slice(0, 8) || '—';
  };

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="sourcing-agents-page" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#1e40af] to-[#3b82f6] flex items-center justify-center shadow-md shadow-blue-500/20">
              <Radar className="w-4.5 h-4.5 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-slate-800 tracking-tight" style={{ fontFamily: 'Georgia, serif' }}>
              Sourcing Agents
            </h1>
          </div>
          <p className="text-sm text-slate-500 ml-11">
            Autonomous agents that continuously hunt, score, and qualify candidates for your open roles.
          </p>
        </div>
        <button
          onClick={openCreate}
          data-testid="new-sourcing-agent-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#1e40af] to-[#3b82f6] text-white text-sm font-semibold rounded-lg shadow-md shadow-blue-500/20 hover:shadow-lg hover:shadow-blue-500/30 transition-all"
        >
          <Plus className="w-4 h-4" />
          New Sourcing Agent
        </button>
      </div>

      {/* Live KPI strip */}
      <StatStrip agents={agents} />

      {/* Body */}
      {loading ? (
        <div className="flex items-center justify-center py-32" data-testid="sourcing-loading">
          <Loader2 className="w-6 h-6 text-slate-300 animate-spin" />
        </div>
      ) : agents.length === 0 ? (
        <EmptyState onCreate={openCreate} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {agents.map((a) => (
            <AgentCard
              key={a.agent_id}
              agent={a}
              jobTitle={jobTitleFor(a.job_id)}
              starting={startingId === a.agent_id}
              stopping={stoppingId === a.agent_id}
              onStart={() => handleStart(a)}
              onStop={() => handleStop(a)}
              onDelete={() => handleDelete(a)}
            />
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <CreateAgentModal
          form={form}
          setForm={setForm}
          jobs={jobs}
          creating={creating}
          onCancel={() => setShowCreate(false)}
          onSubmit={handleCreate}
          addToast={addToast}
        />
      )}
    </div>
  );
}

// ── Stat Strip (live KPI tiles) ───────────────────────────────────────────
function StatStrip({ agents }) {
  const activeNow = agents.filter((a) => ['running', 'hunting'].includes(a.status)).length;
  const candidatesFound = agents.reduce((sum, a) => sum + (a.progress?.found || 0), 0);
  const goalsReached = agents.filter((a) => a.status === 'goal_reached').length;

  return (
    <div className="flex flex-col sm:flex-row gap-3 mb-6" data-testid="sourcing-stat-strip">
      <StatTile label="Active Now" value={activeNow} Icon={Radar} tone="blue" pulsing={activeNow > 0} testid="stat-tile-active" />
      <StatTile label="Candidates Found" value={candidatesFound} Icon={Users} tone="emerald" testid="stat-tile-found" />
      <StatTile label="Goals Reached" value={goalsReached} Icon={Target} tone="green" testid="stat-tile-goals" />
    </div>
  );
}

function StatTile({ label, value, Icon, tone, pulsing = false, testid }) {
  const toneMap = {
    blue:    { icon: 'text-[#2563eb] bg-blue-50',       dot: 'bg-[#2563eb]' },
    emerald: { icon: 'text-emerald-600 bg-emerald-50',  dot: 'bg-emerald-500' },
    green:   { icon: 'text-green-700 bg-green-50',      dot: 'bg-green-500' },
  };
  const t = toneMap[tone] || toneMap.blue;
  return (
    <div data-testid={testid} className="flex-1 flex items-center gap-3 bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 shadow-sm p-4 transition-shadow hover:shadow-md">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${t.icon}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div>
        <div className="flex items-center gap-2">
          <div className="text-2xl font-bold text-slate-800 tabular-nums" style={{ fontFamily: 'Georgia, serif' }}>{value}</div>
          {pulsing && (
            <span className="relative inline-flex" data-testid="stat-tile-active-pulse">
              <span className={`absolute inline-flex h-2 w-2 rounded-full ${t.dot} opacity-60 animate-ping`} />
              <span className={`relative inline-flex h-2 w-2 rounded-full ${t.dot}`} />
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Empty State ───────────────────────────────────────────────────────────
function EmptyState({ onCreate }) {
  return (
    <div className="border-2 border-dashed border-slate-200 rounded-2xl bg-white/50 backdrop-blur-sm p-16 text-center" data-testid="sourcing-empty-state">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-blue-50 to-emerald-50 flex items-center justify-center mb-4">
        <Radar className="w-7 h-7 text-[#2563eb]" />
      </div>
      <h3 className="text-lg font-bold text-slate-800 mb-1.5" style={{ fontFamily: 'Georgia, serif' }}>No sourcing agents yet</h3>
      <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">Launch your first autonomous agent to source, score, and qualify candidates for an open role — 24/7.</p>
      <button onClick={onCreate} data-testid="empty-create-agent-btn" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#2563eb] hover:bg-[#1e40af] text-white text-sm font-semibold rounded-lg transition-colors">
        <Plus className="w-4 h-4" /> Create your first agent
      </button>
    </div>
  );
}

// ── Agent Card ────────────────────────────────────────────────────────────
function AgentCard({ agent, jobTitle, starting, stopping, onStart, onStop, onDelete }) {
  const meta = statusMeta(agent.status || 'idle');
  const StatusIcon = meta.Icon;
  const active = isActiveStatus(agent.status);
  const found = agent.progress?.found ?? 0;
  const goal = agent.progress?.goal ?? agent.max_candidates ?? 0;
  const percent = Math.min(100, Math.max(0, Number(agent.progress?.percent ?? (goal ? (found / goal) * 100 : 0))));

  return (
    <div
      className={`bg-white border rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden ${
        active ? 'border-blue-300 ring-1 ring-blue-100' : 'border-slate-200'
      }`}
      data-testid={`sourcing-agent-card-${agent.agent_id}`}
    >
      {active && (<div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-400 via-blue-500 to-emerald-400 animate-pulse" />)}
      <div className="flex items-start justify-between mb-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-bold text-slate-800 truncate" title={agent.name} style={{ fontFamily: 'Georgia, serif' }}>{agent.name}</h3>
          <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-500">
            <Briefcase className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{jobTitle}</span>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-bold border ${meta.color} flex-shrink-0 ml-2`} data-testid={`agent-status-${agent.agent_id}`}>
          <StatusIcon className={`w-3 h-3 ${meta.pulse ? 'animate-pulse' : ''}`} />
          {meta.label}
        </span>
      </div>
      <div className="mb-4">
        <div className="flex items-center justify-between text-xs mb-1.5">
          <span className="text-slate-500 inline-flex items-center gap-1">
            <Target className="w-3 h-3" />
            {found} / {goal || agent.max_candidates || '—'} qualified
          </span>
          <span className="font-semibold text-slate-700">{Math.round(percent)}%</span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              agent.status === 'goal_reached' || agent.status === 'completed' ? 'bg-emerald-500' : 'bg-gradient-to-r from-[#1e40af] to-[#3b82f6]'
            }`}
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 text-xs text-slate-500 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-400">Min score</div>
          <div className="font-semibold text-slate-700">{agent.min_score ?? 60}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-400">Batches</div>
          <div className="font-semibold text-slate-700">{agent.last_batch_num ?? 0}</div>
        </div>
      </div>
      {agent.last_error && (
        <div className="mb-3 px-2.5 py-2 bg-rose-50 border border-rose-100 rounded-lg text-[11px] text-rose-700 flex items-start gap-1.5">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-[1px]" />
          <span className="truncate" title={agent.last_error}>{agent.last_error}</span>
        </div>
      )}
      <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
        {active ? (
          <button onClick={onStop} disabled={stopping} data-testid={`stop-agent-${agent.agent_id}`}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-semibold rounded-lg transition-colors disabled:opacity-50">
            {stopping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Square className="w-3.5 h-3.5" />} Stop
          </button>
        ) : (
          <button onClick={onStart} disabled={starting} data-testid={`start-agent-${agent.agent_id}`}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-[#2563eb] hover:bg-[#1e40af] text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-50">
            {starting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Start
          </button>
        )}
        <button onClick={onDelete} data-testid={`delete-agent-${agent.agent_id}`} title="Delete agent"
          className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ── Create Modal ───────────────────────────────────────────────────────────
function CreateAgentModal({ form, setForm, jobs, creating, onCancel, onSubmit, addToast }) {
  const [generatingGoal, setGeneratingGoal] = useState(false);

  const handleGenerateGoal = async () => {
    if (!form.job_id) {
      addToast('Select a job first to generate the goal description', 'error');
      return;
    }
    setGeneratingGoal(true);
    try {
      const { data } = await api.post('/sourcing-agents/generate-goal', { job_id: form.job_id });
      setForm((prev) => ({ ...prev, goal: data.goal }));
      addToast('Goal description generated!', 'success');
    } catch (err) {
      addToast(err.response?.data?.detail || 'Failed to generate goal', 'error');
    } finally {
      setGeneratingGoal(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onCancel} data-testid="create-agent-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-blue-50/50 to-white">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#1e40af] to-[#3b82f6] flex items-center justify-center">
              <Radar className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Georgia, serif' }}>New Sourcing Agent</h2>
          </div>
          <button onClick={onCancel} className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={onSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Agent Name *</label>
            <input
              data-testid="create-agent-name"
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Senior Python Engineers — SF"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Target Job *</label>
            <select
              data-testid="create-agent-job"
              value={form.job_id}
              onChange={(e) => setForm({ ...form, job_id: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 bg-white"
              required
            >
              <option value="">Select a job…</option>
              {jobs.map((j) => (
                <option key={j.job_id} value={j.job_id}>{j.title}</option>
              ))}
            </select>
          </div>

          {/* Goal Description with AI Generate button */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-semibold text-slate-600">Goal Description (optional)</label>
              <button
                type="button"
                data-testid="generate-goal-btn"
                onClick={handleGenerateGoal}
                disabled={generatingGoal || !form.job_id}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-lg bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-sm hover:from-violet-600 hover:to-purple-700 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                title={!form.job_id ? 'Select a job first' : 'Auto-generate from job description using Gemini AI'}
              >
                {generatingGoal
                  ? <><Loader2 className="w-3 h-3 animate-spin" /> Generating…</>
                  : <><Sparkles className="w-3 h-3" /> AI Generate</>
                }
              </button>
            </div>
            <textarea
              data-testid="create-agent-goal"
              value={form.goal}
              onChange={(e) => setForm({ ...form, goal: e.target.value })}
              rows={3}
              placeholder="Describe ideal candidates, e.g. 'Senior backend engineers with 5+ yrs Python + AWS, fintech experience preferred.' — or click ✨ AI Generate above."
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 resize-none"
            />
            {form.goal && (
              <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                Goal set — you can edit it freely.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Min Score</label>
              <input
                data-testid="create-agent-min-score"
                type="number" min={0} max={100}
                value={form.min_score}
                onChange={(e) => setForm({ ...form, min_score: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Candidate Goal</label>
              <input
                data-testid="create-agent-max-candidates"
                type="number" min={1} max={500}
                value={form.max_candidates}
                onChange={(e) => setForm({ ...form, max_candidates: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onCancel}
              className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">Cancel</button>
            <button type="submit" data-testid="submit-create-agent" disabled={creating}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1e40af] to-[#3b82f6] text-white text-sm font-semibold rounded-lg shadow-md shadow-blue-500/20 hover:shadow-lg transition-all disabled:opacity-60">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Create agent
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
